import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "chat-summary__container" }
const _hoisted_2 = { class: "chat-summary__content" }
const _hoisted_3 = { class: "chat-summary__header" }
const _hoisted_4 = { class: "chat-summary__caption" }
const _hoisted_5 = { key: 0 }

import type { ChatTask, TaskProcessingResponse } from '../../types/index.ts'

import { showError } from '@nextcloud/dialogs'
import { t } from '@nextcloud/l10n'
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import NcAssistantContent from '@nextcloud/vue/components/NcAssistantContent'
import NcAssistantIcon from '@nextcloud/vue/components/NcAssistantIcon'
import NcButton from '@nextcloud/vue/components/NcButton'
import IconUnfoldLessHorizontal from 'vue-material-design-icons/UnfoldLessHorizontal.vue'
import IconUnfoldMoreHorizontal from 'vue-material-design-icons/UnfoldMoreHorizontal.vue'
import { useGetToken } from '../../composables/useGetToken.ts'
import { TASK_PROCESSING } from '../../constants.ts'
import { deleteTaskById, getTaskById } from '../../services/coreService.ts'
import { useChatExtrasStore } from '../../stores/chatExtras.ts'
import CancelableRequest from '../../utils/cancelableRequest.js'

type TaskProcessingCancelableRequest = {
	request: (taskId: number) => TaskProcessingResponse
	cancel: () => void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NewMessageChatSummary',
  setup(__props) {

let getTaskInterval: NodeJS.Timeout | undefined
const cancelGetTask: Record<string, TaskProcessingCancelableRequest['cancel']> = {}

const chatSummaryRef = useTemplateRef<HTMLParagraphElement>('chatSummaryRef')
const collapsed = ref(true)
const isTextMoreThanOneLine = ref(false)

const loading = ref(true)
const cancelling = ref(false)

const chatExtrasStore = useChatExtrasStore()

const token = useGetToken()

const chatSummaryMessage = ref('')
const chatSummaryCaption = computed(() => {
	return loading.value
		? t('spreed', 'Generating summary of unread messages …')
		: t('spreed', 'Summary is AI generated and might contain mistakes')
})

watch(chatSummaryMessage, () => {
	nextTick(() => {
		setIsTextMoreThanOneLine()
	})
}, { immediate: true })

onBeforeUnmount(() => {
	Object.values(cancelGetTask).forEach((cancelFn) => cancelFn())
})

watch(token, (newValue, oldValue) => {
	// Cancel pending requests when leaving room
	if (oldValue && cancelGetTask[oldValue]) {
		cancelGetTask[oldValue]?.()
		clearInterval(getTaskInterval)
		getTaskInterval = undefined
	}
	if (newValue) {
		loading.value = true
		chatSummaryMessage.value = ''
		checkScheduledTasks(newValue)
	}
}, { immediate: true })

/**
 *
 * @param token conversation token
 */
function checkScheduledTasks(token: string) {
	const taskQueue: ChatTask[] = chatExtrasStore.getChatSummaryTaskQueue(token)

	if (!taskQueue.length) {
		return
	}

	for (const task of taskQueue) {
		if (task.summary) {
			// Task is already finished, checking next one
			continue
		}
		const { request, cancel } = CancelableRequest(getTaskById) as TaskProcessingCancelableRequest
		cancelGetTask[token] = cancel

		getTaskInterval = setInterval(() => {
			getTask(token, request, task)
		}, 5000)
		return
	}

	// There was no return, so checking all tasks are finished
	chatSummaryMessage.value = chatExtrasStore.getChatSummary(token)
	loading.value = false
}

/**
 *
 * @param token conversation token
 * @param request cancelable request to get task from API
 * @param task task object
 */
async function getTask(token: string, request: TaskProcessingCancelableRequest['request'], task: ChatTask) {
	try {
		const response = await request(task.taskId)
		const status = response.data.ocs.data.task.status
		switch (status) {
			case TASK_PROCESSING.STATUS.SUCCESSFUL: {
			// Task is completed, proceed to the next task
				const summary = response.data.ocs.data.task.output?.output as string || ''
				chatExtrasStore.storeChatSummary(token, task.fromMessageId, summary)
				clearInterval(getTaskInterval)
				getTaskInterval = undefined
				checkScheduledTasks(token)
				break
			}
			case TASK_PROCESSING.STATUS.FAILED:
			case TASK_PROCESSING.STATUS.UNKNOWN:
			case TASK_PROCESSING.STATUS.CANCELLED: {
			// Task is likely failed, proceed to the next task
				showError(t('spreed', 'Error occurred during a summary generation'))
				cancelSummary()
				break
			}
			case TASK_PROCESSING.STATUS.SCHEDULED:
			case TASK_PROCESSING.STATUS.RUNNING:
			default: {
			// Task is still processing, scheduling next request
				break
			}
		}
	} catch (error) {
		if (CancelableRequest.isCancel(error)) {
			return
		}
		console.error('Error getting chat summary:', error)
	}
}

/**
 *
 */
function dismissSummary() {
	Object.values(cancelGetTask).forEach((cancelFn) => cancelFn())
	clearInterval(getTaskInterval)
	getTaskInterval = undefined
	chatExtrasStore.dismissChatSummary(token.value)
}

/**
 *
 */
async function cancelSummary() {
	cancelling.value = true
	const taskQueue: ChatTask[] = chatExtrasStore.getChatSummaryTaskQueue(token.value)
	for await (const task of taskQueue) {
		await deleteTaskById(task.taskId)
	}
	cancelling.value = false
	dismissSummary()
}

/**
 *
 */
function toggleCollapsed() {
	collapsed.value = !collapsed.value
}

/**
 *
 */
function setIsTextMoreThanOneLine() {
	if (!chatSummaryRef.value) {
		return
	}
	isTextMoreThanOneLine.value = chatSummaryRef.value.scrollHeight > chatSummaryRef.value.clientHeight
	collapsed.value = !isTextMoreThanOneLine.value
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NcAssistantContent), { class: "chat-summary" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(NcAssistantIcon), { class: "chat-summary__icon" }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("p", _hoisted_4, _toDisplayString(chatSummaryCaption.value), 1),
            (loading.value)
              ? (_openBlock(), _createBlock(_unref(NcButton), {
                  key: 0,
                  variant: "tertiary",
                  disabled: cancelling.value,
                  onClick: cancelSummary
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Cancel')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : (chatSummaryMessage.value)
                ? (_openBlock(), _createBlock(_unref(NcButton), {
                    key: 1,
                    variant: "tertiary",
                    onClick: dismissSummary
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Dismiss')), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
            (isTextMoreThanOneLine.value)
              ? (_openBlock(), _createBlock(_unref(NcButton), {
                  key: 2,
                  variant: "tertiary",
                  title: collapsed.value ? _unref(t)('spreed', 'Expand') : _unref(t)('spreed', 'Collapse'),
                  "aria-label": collapsed.value ? _unref(t)('spreed', 'Expand') : _unref(t)('spreed', 'Collapse'),
                  onClick: toggleCollapsed
                }, {
                  icon: _withCtx(() => [
                    (collapsed.value)
                      ? (_openBlock(), _createBlock(IconUnfoldMoreHorizontal, {
                          key: 0,
                          size: 20
                        }))
                      : (_openBlock(), _createBlock(IconUnfoldLessHorizontal, {
                          key: 1,
                          size: 20
                        }))
                  ]),
                  _: 1
                }, 8, ["title", "aria-label"]))
              : _createCommentVNode("", true)
          ]),
          (loading.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref(t)('spreed', 'This might take a moment')), 1))
            : (_openBlock(), _createElementBlock("p", {
                key: 1,
                ref_key: "chatSummaryRef",
                ref: chatSummaryRef,
                class: _normalizeClass(["chat-summary__message", { 'chat-summary__message--collapsed': collapsed.value }])
              }, _toDisplayString(chatSummaryMessage.value), 3))
        ])
      ])
    ]),
    _: 1
  }))
}
}

})